import { useState, useEffect } from 'react';
import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import { Grid, TextField, Button, MenuItem, InputAdornment } from '@material-ui/core';
import { MatcherGroups, Matchers, Companies } from '../../api/api';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { Company } from '../../models/location.interface';
import { RulesType, defaultRulesType, Matcher, MatcherGroup, defaultMatcherGroup } from '../../models/matcher.interface';
import { KeyTypes, MatcherOperator, MatcherType, StringOperatorTypes } from '../../app/enums';
import { isEmpty } from 'lodash';


const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        alertNoMargin: {
            width: '100%',
        },
        dialogContent: {
            overflow: 'auto',
        },
        gridSpacer: {
            minHeight: '12px',
        },
    }),
);

interface IEditRuleDialog {
    open: boolean
    onClose: () => void
    make: string,
    model: string,
    minYear: string,
    maxYear: string,
    keyType: string,
    price: string,
    company: string,
    id: string
}

interface RuleError {
    make: string
    makeOperator: string
    model: string
    modelOperator: string
    keyType: string
    minYear: string
    maxYear: string
    price: string
    company: string
}

const defaultRuleError: RuleError = {
    make: '',
    makeOperator: '',
    model: '',
    modelOperator: '',
    keyType: '',
    minYear: '',
    maxYear: '',
    price: '',
    company: ''
}

export const EditRuleDialog = (props: IEditRuleDialog) => {
    const classes = useStyles();
    const [error, setError] = useState<string>('');
    const [companies, setCompanies] = useState<Company[]>([]);
    const [newRule, setNewRule] = useState<RulesType>(defaultRulesType);
    const [currentMatcherGroup, setCurrentMatcherGroup] = useState<MatcherGroup>(defaultMatcherGroup);
    const [disabled, setDisabled] = useState(false);
    const [ruleError, setRuleError] = useState<RuleError>({ ...defaultRuleError });

    useEffect(() => {
        Companies.getAllCompanies()
            .then((comps) => {
                setCompanies(comps.sort((a, b) => (a.name > b.name) ? 1 : -1));
            }).catch((err) => {
                setError(err.response.data.message);
            })
    }, []);

    useEffect(() => {
        setNewRule(defaultRulesType)
    }, [props])


    const handleCompanyChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { value } = e.target;
        setNewRule({ ...newRule, ['company']: value });
    };

    const handleSelectOperatorChange = (event: React.ChangeEvent<{ value: unknown }>, fieldName: string) => {
        const value: string = event.target.value as string;

        if (fieldName === 'keyType' && value === 'UNSUPPORTED') {
            setDisabled(true)
            setNewRule({ ...newRule, price: 0 })
        } else {
            setDisabled(false)
        }

        setNewRule({ ...newRule, [fieldName]: value })
    };

    const validateField = (e: React.FormEvent<EventTarget>) => {
        let target = e.target as HTMLInputElement

        if (target.value === undefined ?? target.value.length < 1)
            setRuleError({ ...ruleError, [target.id]: 'Required' });
        else
            setRuleError({ ...ruleError, [target.id]: '' });
    }

    const validatePrice = () => {
        if (newRule.keyType !== KeyTypes.UNSUPPORTED.toUpperCase()
            && newRule.price < 1) {
            setRuleError({ ...ruleError, price: 'Please enter valid price' })
        } else {
            setRuleError({ ...ruleError, price: '' })
        }
    }

    const validateYear = (e: React.FormEvent<EventTarget>) => {
        let target = e.target as HTMLInputElement

        if (+target.value < 0) {
            setRuleError({ ...ruleError, [target.id]: 'Please enter valid year' })
        } else {
            setRuleError({ ...ruleError, [target.id]: '' })
        }
    }

    const handleChange = (e: React.FormEvent<EventTarget>) => {
        let target = e.target as HTMLInputElement;
        setNewRule({ ...newRule, [target.id]: target.value });
    }

    const handleSave = (e: any) => {
        e.preventDefault()
        MatcherGroups.getMatcherGroup(props.id).then((mg) => {
            setCurrentMatcherGroup(mg)
        })

        Matchers.getAllMatchers(props.id).then((matcherGroups) => {
            console.log(matcherGroups)
            let matcherUpdatePromises = [];
            let matcherCreatePromises = [];

            let existingMakeMatcher = matcherGroups.find(matcher => matcher.type === MatcherType.MAKE)
            if (newRule.make) {
                if (existingMakeMatcher) {
                    let newMatcher = {
                        id: existingMakeMatcher.id,
                        type: MatcherType.MAKE,
                        operator: newRule.makeOperator,
                        value: newRule.make
                    } as Matcher
                    matcherUpdatePromises.push(
                        Matchers.updateMatcher(
                            props.id,
                            newMatcher)
                    );
                } else {
                    matcherCreatePromises.push(
                        Matchers.createMatcher(
                            props.id,
                            MatcherType.MAKE,
                            (newRule.makeOperator ?? MatcherOperator.EQUALS),
                            newRule.make)
                    );
                }
            }

            let existingModelMatcher = matcherGroups.find(matcher => matcher.type === MatcherType.MODEL)
            if (newRule.model) {
                if (existingModelMatcher) {
                    let newMatcher = {
                        id: existingModelMatcher.id,
                        type: MatcherType.MODEL,
                        operator: newRule.modelOperator,
                        value: newRule.model
                    } as Matcher
                    matcherUpdatePromises.push(
                        Matchers.updateMatcher(
                            props.id,
                            newMatcher)
                    );
                } else {
                    matcherCreatePromises.push(
                        Matchers.createMatcher(
                            props.id,
                            MatcherType.MODEL,
                            (newRule.modelOperator ?? MatcherOperator.EQUALS),
                            newRule.model)
                    );
                }

            }

            let existingCompanyMatcher = matcherGroups.find(matcher => matcher.type === MatcherType.COMPANY)
            if (newRule.company) {
                if (existingCompanyMatcher) {
                    let newMatcher = {
                        id: existingCompanyMatcher.id,
                        type: MatcherType.COMPANY,
                        operator: MatcherOperator.EQUALS,
                        value: newRule.company
                    } as Matcher
                    matcherUpdatePromises.push(
                        Matchers.updateMatcher(
                            props.id,
                            newMatcher)
                    );
                } else {
                    matcherCreatePromises.push(
                        Matchers.createMatcher(
                            props.id,
                            MatcherType.COMPANY,
                            MatcherOperator.EQUALS,
                            newRule.company)
                    );
                }

            }

            let existingMinYearMatcher = matcherGroups.find(matcher => matcher.type === MatcherType.YEAR
                && matcher.operator === MatcherOperator.GREATER_THAN_OR_EQUALS)
            if (newRule.minYear) {
                if (existingMinYearMatcher) {
                    let newMatcher = {
                        id: existingMinYearMatcher.id,
                        type: MatcherType.YEAR,
                        operator: MatcherOperator.GREATER_THAN_OR_EQUALS,
                        value: newRule.minYear
                    } as Matcher
                    matcherUpdatePromises.push(
                        Matchers.updateMatcher(
                            props.id,
                            newMatcher)
                    );
                } else {
                    matcherCreatePromises.push(
                        Matchers.createMatcher(
                            props.id,
                            MatcherType.YEAR,
                            MatcherOperator.GREATER_THAN_OR_EQUALS,
                            newRule.minYear)
                    );
                }
            }

            let existingMaxYearMatcher = matcherGroups.find(matcher => matcher.type === MatcherType.YEAR
                && matcher.operator === MatcherOperator.LESS_THAN_OR_EQUALS)
            if (newRule.maxYear) {
                if (existingMaxYearMatcher) {
                    let newMatcher = {
                        id: existingMaxYearMatcher.id,
                        type: MatcherType.YEAR,
                        operator: MatcherOperator.LESS_THAN_OR_EQUALS,
                        value: newRule.maxYear
                    } as Matcher
                    matcherUpdatePromises.push(
                        Matchers.updateMatcher(
                            props.id,
                            newMatcher)
                    );
                } else {
                    matcherCreatePromises.push(
                        Matchers.createMatcher(
                            props.id,
                            MatcherType.YEAR,
                            MatcherOperator.LESS_THAN_OR_EQUALS,
                            newRule.maxYear)
                    );
                }
            }

            if (newRule.keyType || newRule.price) {
                MatcherGroups.updateMatcherGroup(props.id, currentMatcherGroup.allRequired, newRule.keyType ?? currentMatcherGroup.keyType, newRule.price !== 0 ? newRule.price : currentMatcherGroup.price)
            }
            Promise.all(matcherCreatePromises)
                .then((matchers) => {
                    console.log(matchers)
                    props.onClose();
                })
                .catch((e) => {
                    setError(e.response.data.message);
                });

            Promise.all(matcherUpdatePromises)
                .then((matchers) => {
                    console.log(matchers)
                    props.onClose();
                })
                .catch((e) => {
                    setError(e.response.data.message);
                });
        })
        props.onClose()
    }
    return (
        <Dialog
            onClose={props.onClose}
            aria-label="addRuleDialog"
            open={props.open}
            maxWidth="xs"
            fullWidth
        >
            <DialogTitle aria-label="addRuleDialogTitle">
                Add Rule
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
                <Grid
                    container
                    justifyContent="space-between"
                    spacing={1}
                >
                    <Grid item xs={6}>
                        <TextField
                            id="make"
                            aria-label="make"
                            margin="normal"
                            variant="outlined"
                            fullWidth
                            placeholder={props.make}
                            onChange={handleChange}
                            defaultValue={props.make}
                            value={newRule && newRule.make && newRule.make?.length > 0 ? newRule.make : props.make}
                            inputProps={{ "data-testid": "makeInput" }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            label="Make Operator"
                            id="makeOperator"
                            aria-label="makeOperator"
                            margin="normal"
                            variant="outlined"
                            fullWidth
                            value={newRule.makeOperator}
                            onChange={(e) => handleSelectOperatorChange(e, 'makeOperator')}
                            disabled={isEmpty(props.make) && isEmpty(newRule.make)}
                            inputProps={{ "data-testid": "makeOperatorInput" }}
                            select
                        >
                            {Object.keys(StringOperatorTypes).map((operatorType) => (
                                <MenuItem key={operatorType} value={operatorType}>
                                    {StringOperatorTypes[operatorType as keyof typeof StringOperatorTypes]}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>
                <Grid
                    container
                    justifyContent="space-between"
                    spacing={1}
                >
                    <Grid item xs={6}>
                        <TextField
                            id="model"
                            aria-label="model"
                            margin="normal"
                            variant="outlined"
                            fullWidth
                            placeholder={props.model}
                            onChange={handleChange}
                            defaultValue={props.model}
                            value={newRule && newRule.model && newRule.model?.length > 0 ? newRule.model : props.model}
                            inputProps={{ "data-testid": "makeInput" }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            label="Model Operator"
                            id="modelOperator"
                            aria-label="modelOperator"
                            margin="normal"
                            variant="outlined"
                            fullWidth
                            value={newRule.modelOperator}
                            disabled={isEmpty(props.model) && isEmpty(newRule.model)}
                            onChange={(e) => handleSelectOperatorChange(e, 'modelOperator')}
                            inputProps={{ "data-testid": "modelOperatorInput" }}
                            select
                        >
                            {Object.keys(StringOperatorTypes).map((operatorType) => (
                                <MenuItem key={operatorType} value={operatorType}>
                                    {StringOperatorTypes[operatorType as keyof typeof StringOperatorTypes]}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>
                <Grid
                    container
                    justifyContent="space-between"
                    spacing={1}
                >
                    <Grid item xs={6}>
                        <TextField
                            id="minYear"
                            aria-label="minYear"
                            inputProps={{ "data-testid": "minYearInput" }}
                            label="Min Year"
                            fullWidth
                            margin="normal"
                            variant="outlined"
                            value={newRule.minYear ?? props.minYear}
                            type="number"
                            onChange={handleChange}
                            onBlur={validateYear}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            id="maxYear"
                            aria-label="maxYear"
                            inputProps={{ "data-testid": "maxYearInput" }}
                            label="Max Year"
                            fullWidth
                            margin="normal"
                            value={newRule.maxYear ?? props.maxYear}
                            variant="outlined"
                            type="number"
                            onChange={handleChange}
                            onBlur={validateYear}
                        />
                    </Grid>
                </Grid>
                <Grid container>
                    <TextField
                        id="keyTypes"
                        aria-label="keyTypes"
                        label="Key Types"
                        fullWidth
                        margin="normal"
                        variant="outlined"
                        select
                        required
                        value={isEmpty(newRule.keyType) ? props.keyType : newRule.keyType}
                        onChange={(e) => handleSelectOperatorChange(e, 'keyType')}
                        error={ruleError.keyType.length > 0}
                        helperText={ruleError.keyType}
                        onBlur={validateField}
                        defaultValue="UNSUPPORTED"
                    >
                        {Object.keys(KeyTypes).map((keyType) => (
                            <MenuItem key={keyType} value={keyType}>
                                {KeyTypes[keyType as keyof typeof KeyTypes]}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid container>
                    <TextField
                        id="price"
                        aria-label="price"
                        label="Price"
                        fullWidth
                        margin="normal"
                        variant="outlined"
                        disabled={disabled}
                        type="number"
                        value={newRule.price !== 0 ? newRule.price : props.price}
                        onChange={handleChange}
                        onBlur={validatePrice}
                        error={ruleError.price.length > 0}
                        helperText={ruleError.price}
                        required
                        InputProps={{
                            startAdornment:
                                <InputAdornment position="start">
                                    $
                                </InputAdornment>,
                        }}
                    />
                </Grid>
                <Grid container>
                    <TextField
                        id="company"
                        aria-label="company"
                        label="Company"
                        fullWidth
                        margin="normal"
                        variant="outlined"
                        select
                        onChange={handleCompanyChange}
                        value={isEmpty(newRule.company) ? props.company : newRule.company}
                        inputProps={{ "data-testid": "companyList" }}
                    >
                        <MenuItem key="any" value="-1">
                            Any
                        </MenuItem>
                        {companies.map((comp) => (
                            <MenuItem key={comp.name} value={comp.name}>
                                {comp.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button
                    aria-label="ruleDialogCancel"
                    variant="outlined"
                    color="primary"
                    onClick={props.onClose}
                >
                    Cancel
                </Button>
                <Button
                    aria-label="ruleDialogSub(mit"
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleSave}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}
